/**
 * MessageBubble — Forensic Dialogue Bubble
 * ---------------------------------------------------------------
 * Restrained motion language: opacity fade + tiny vertical drift
 * only. NO bounce, no scale-pop, no kinetic typography. Resonant
 * turns get an isolation treatment — surrounding bubbles dim, the
 * focal phrase is held longer, ambient glow softens around it.
 */

import { useCurrentFrame, interpolate } from "remotion";
import { PALETTE } from "../utils/timing";
import type { TurnFrame } from "../utils/timing";

interface Props {
  turn: TurnFrame;
  visible: boolean;
  /** Stack offset (0 = newest at bottom). Older bubbles drift up + dim. */
  stackOffset: number;
  /** Currently inside a resonant isolation window (any bubble). */
  isolationActive?: boolean;
  /** Is THIS bubble the one being isolated. */
  isFocal?: boolean;
}

const ROLE_STYLE = (role: TurnFrame["role"]) => {
  if (role === "them") {
    return {
      bg: PALETTE.themBubble,
      border: PALETTE.themBorder,
      label: "THEM",
      labelColor: "#e89a9a",
      align: "flex-start" as const,
    };
  }
  if (role === "you_alt") {
    return {
      bg: PALETTE.altBubble,
      border: PALETTE.altBorder,
      label: "YOU · ALT",
      labelColor: "#9be0bf",
      align: "flex-end" as const,
    };
  }
  return {
    bg: PALETTE.youBubble,
    border: PALETTE.youBorder,
    label: "YOU",
    labelColor: PALETTE.cyanSoft,
    align: "flex-end" as const,
  };
};

export const MessageBubble: React.FC<Props> = ({
  turn,
  visible,
  stackOffset,
  isolationActive = false,
  isFocal = false,
}) => {
  const frame = useCurrentFrame();
  if (!visible) return null;

  const localFrame = frame - turn.startFrame;

  // Restrained entrance: slow opacity fade + subtle vertical drift.
  // No spring, no bounce. Forensic, not festive.
  const enterFade = interpolate(localFrame, [0, 22], [0, 1], {
    extrapolateRight: "clamp",
  });
  const enterLift = interpolate(localFrame, [0, 26], [14, 0], {
    extrapolateRight: "clamp",
  });

  // Calm typewriter — slightly slower for resonant turns
  const revealSpan = Math.max(1, turn.revealFrame - turn.startFrame);
  const revealLen = Math.max(
    0,
    Math.min(turn.text.length, Math.round((localFrame / revealSpan) * turn.text.length))
  );
  const revealedText = turn.text.slice(0, revealLen);
  const cursor = revealLen < turn.text.length ? "▏" : "";

  // Stack drift — older bubbles fade and lift slowly
  const stackY = -stackOffset * 32;
  const stackOpacity = interpolate(
    stackOffset,
    [0, 1, 2, 3, 4],
    [1, 0.5, 0.24, 0.1, 0.04],
    { extrapolateRight: "clamp" }
  );

  // Resonant isolation: non-focal bubbles dim further; focal gets soft glow
  const isolationDim = isolationActive && !isFocal ? 0.28 : 1;
  const focalGlow = isFocal && isolationActive
    ? `0 0 60px rgba(201, 169, 106, 0.18), 0 0 1px rgba(201, 169, 106, 0.4)`
    : "none";

  const style = ROLE_STYLE(turn.role);
  const opacity = enterFade * stackOpacity * isolationDim;
  const translateY = enterLift + stackY;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: style.align,
        width: "100%",
        marginBottom: 28,
        opacity,
        transform: `translate3d(0, ${translateY}px, 0)`,
        transition: "none",
      }}
    >
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 16,
          letterSpacing: "0.42em",
          color: style.labelColor,
          marginBottom: 12,
          textTransform: "uppercase",
          opacity: 0.75,
        }}
      >
        T{String(turn.index).padStart(2, "0")} · {style.label}
        {turn.resonant && (
          <span style={{ marginLeft: 16, color: PALETTE.goldSoft }}>
            · RESONANT
          </span>
        )}
      </div>
      <div
        style={{
          maxWidth: "84%",
          padding: "28px 34px",
          backgroundColor: style.bg,
          borderLeft: style.align === "flex-start" ? `2px solid ${style.border}` : "none",
          borderRight: style.align === "flex-end" ? `2px solid ${style.border}` : "none",
          borderRadius: 4,
          color: PALETTE.silver,
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontSize: turn.resonant && isFocal ? 50 : 42,
          lineHeight: 1.32,
          fontWeight: turn.resonant && isFocal ? 500 : 450,
          letterSpacing: "-0.005em",
          textShadow: "0 1px 2px rgba(0,0,0,0.6)",
          boxShadow: focalGlow,
        }}
      >
        {revealedText}
        <span style={{ color: PALETTE.cyanSoft, opacity: 0.7 }}>{cursor}</span>
      </div>
    </div>
  );
};
